import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { StyleProvider } from "@ant-design/cssinjs"
import { useSplitScreenStore } from "store/splitScreen.store"
import { safeRender, type RootEntry } from "../core/shadowPortal"
import { PageNavigationBar } from "../PageNavigationBar/PageNavigationBar"
import type { MutableRefObject } from "react"
import type { WebViewerInstance, Core } from "@pdftron/webviewer"
import type { TViewerMode } from "store/viewer.store"
import type { TPageElevationData } from "types/elevationReferences.type"

// How long to honor the chevron / page-name target after a programmatic navigation,
// so Apryse's scroll-animation pageNumberUpdated bursts can't snap us back.
const PAGE_NAV_TARGET_LOCK_MS = 1000

type TPageNavigationCtx = {
	instanceRef: MutableRefObject<WebViewerInstance | null>
	mode: TViewerMode
	isModalOpen: boolean
	isDocumentLoaded: boolean
	relatedObjects: TPageElevationData[] | undefined
	relatedObjectsRef: MutableRefObject<TPageElevationData[] | undefined>
	getShadowRoot: () => ShadowRoot | undefined
	pageNavEntryRef: MutableRefObject<RootEntry | null>
	// --- Split screen (REQ-399): one instance of this hook per pane ------------------------------
	// Which DocumentViewer this bar reports on and navigates. Must be referentially stable.
	// Defaults to the primary viewer, i.e. the single-pane behavior.
	getDocViewer?: () => Core.DocumentViewer | null
	// The primary pane drives zoom through the UI API (which also re-applies FitMode); the split
	// pane must not, because UI zoom acts on whichever viewer Apryse considers active.
	isPrimaryPane?: boolean
	// Bumped when entering/exiting split screen re-mounts the document area. Re-attaches the
	// listeners — the scroll element they were bound to is detached by then, which is exactly why
	// the bar stopped appearing after entering split screen.
	layoutEpoch?: number
	// False for the split pane while split screen is closed: detaches listeners and hides the bar.
	isEnabled?: boolean
}

// Owns the floating page-navigation bar (page pills + page-name jump). This is a fully self-
// contained editor affordance: it holds its own page state and
// the target-lock that shields programmatic jumps from Apryse's intermediate pageNumberUpdated
// bursts. The orchestrator wires it up by calling `registerPageNavListeners(docViewer)` once,
// synchronously, from inside its WebViewer init (so the listeners are attached before the first
// `documentLoaded` can fire — same timing as when this lived inline). The bar is always shown
// while a document is loaded in the editor (#1398 — it used to fade out after 2s idle).
// `isPageNavVisible` is returned because the orchestrator's overlay-reposition effect keys off it. #1119
export const usePageNavigation = ({
	instanceRef,
	mode,
	isModalOpen,
	isDocumentLoaded,
	relatedObjects,
	relatedObjectsRef,
	getShadowRoot,
	pageNavEntryRef,
	getDocViewer,
	isPrimaryPane = true,
	layoutEpoch = 0,
	isEnabled = true,
}: TPageNavigationCtx) => {
	const pageNavTargetRef = useRef<number | null>(null)
	const pageNavTargetClearTimerRef = useRef<number | null>(null)
	// Removes whatever the last attachListeners call bound (Core events + the scroll listener).
	const detachListenersRef = useRef<(() => void) | null>(null)
	const [currentPage, setCurrentPage] = useState(1)
	// Read live, not passed in: the bar is rendered through Apryse's own lifecycle and a prop would
	// be captured once. Only used to decide whether a page jump may override the pane's zoom.
	const isSplitActive = useSplitScreenStore((state) => state.isSplitActive)
	const [totalPages, setTotalPages] = useState(0)
	// Page-nav overlay is an editor affordance only.
	const isPageNavVisible = isModalOpen && isEnabled && isDocumentLoaded && totalPages > 0 && mode === "editor"

	const resetPageNavTimers = useCallback(() => {
		if (pageNavTargetClearTimerRef.current != null) {
			window.clearTimeout(pageNavTargetClearTimerRef.current)
			pageNavTargetClearTimerRef.current = null
		}
		pageNavTargetRef.current = null
	}, [])

	// Attach the page-nav-driving listeners to a documentViewer. Called once from the orchestrator's
	// init, synchronously, so the documentLoaded listener is in place before the initial document
	// finishes loading — and again whenever `layoutEpoch` changes (split screen re-mounts the
	// document area, detaching the scroll element these were bound to). Idempotent: the previous
	// binding is always removed first.
	const registerPageNavListeners = useCallback((docViewer: Core.DocumentViewer | null) => {
		detachListenersRef.current?.()
		detachListenersRef.current = null
		if (!docViewer) return

		const onDocumentLoaded = () => {
			setTotalPages(docViewer.getPageCount())
			setCurrentPage(docViewer.getCurrentPage())
		}

		const onPageNumberUpdated = (page: number) => {
			// During programmatic chevron / page-name navigation, Apryse's scroll
			// animation can briefly fire pageNumberUpdated for the page being left
			// before settling on the target. Ignore those intermediate events.
			if (pageNavTargetRef.current != null && page !== pageNavTargetRef.current) return
			setCurrentPage(page)
		}

		docViewer.addEventListener("documentLoaded", onDocumentLoaded)
		docViewer.addEventListener("pageNumberUpdated", onPageNumberUpdated)

		// Re-binding after a layout change (or attaching to the split pane, whose document was
		// loaded before this ran) means documentLoaded has already fired — seed the counts now,
		// otherwise totalPages stays 0 and the bar never renders.
		if (docViewer.getDocument()) onDocumentLoaded()

		detachListenersRef.current = () => {
			docViewer.removeEventListener("documentLoaded", onDocumentLoaded)
			docViewer.removeEventListener("pageNumberUpdated", onPageNumberUpdated)
		}
	}, [])

	// Re-attach on every viewer layout change (split screen enter/exit) and when this pane is
	// enabled/disabled. The orchestrator's init call above covers the very first binding.
	useEffect(() => {
		if (!isEnabled) {
			detachListenersRef.current?.()
			detachListenersRef.current = null
			return
		}
		const docViewer = getDocViewer ? getDocViewer() : (instanceRef.current?.Core.documentViewer ?? null)
		if (docViewer) registerPageNavListeners(docViewer)
	}, [getDocViewer, instanceRef, isDocumentLoaded, isEnabled, layoutEpoch, registerPageNavListeners])

	const currentPageName = useMemo<string | null>(() => {
		if (!relatedObjects) return null
		// related_objects' page_number is 0-based; the WebViewer uses 1-based pages.
		const page = relatedObjects.find((p) => p.page_number === currentPage - 1)
		return page?.page_name && page.page_name.trim() !== "" ? page.page_name : null
	}, [relatedObjects, currentPage])

	const handlePageChange = useCallback(
		(page: number) => {
			const instance = instanceRef.current
			const dv = getDocViewer ? getDocViewer() : (instance?.Core.documentViewer ?? null)
			if (!instance || !dv) return
			pageNavTargetRef.current = page
			setCurrentPage(page)
			if (pageNavTargetClearTimerRef.current != null) {
				window.clearTimeout(pageNavTargetClearTimerRef.current)
			}
			pageNavTargetClearTimerRef.current = window.setTimeout(() => {
				pageNavTargetRef.current = null
				pageNavTargetClearTimerRef.current = null
			}, PAGE_NAV_TARGET_LOCK_MS)
			// Reset to a fixed 100% zoom around the navigation. Apryse re-applies the
			// active FitMode (FitWidth/FitPage) on page change, which silently
			// overrides a numeric zoom set just once — so we:
			//   1) switch FitMode to Zoom (no auto-fit) before the jump
			//   2) jump to the page
			//   3) reapply zoom on the next pageComplete for THIS page, after
			//      Apryse's own post-jump layout has run
			//
			// SPLIT SCREEN: no zoom override at all. 100% assumes a full-width viewer; in a pane a
			// few hundred pixels wide it frames the sheet's top-left CORNER, which is what "the pills
			// scroll the page to the top left" is. Both panes sit in a FIT zoom mode by design, so the
			// right behaviour here is to leave the framing alone and only change the page — Apryse
			// re-fits it, and the whole sheet stays visible at whatever zoom the pane is using.
			//
			// The UI zoom API targets whichever viewer Apryse considers active, so the split pane
			// uses the per-viewer Core call instead — otherwise navigating in the right pane would
			// re-zoom the left one (REQ-399).
			if (!isSplitActive) {
				if (isPrimaryPane) {
					instance.UI.setFitMode(instance.UI.FitMode.Zoom)
					instance.UI.setZoomLevel("100%")
				} else {
					dv.zoomTo(1)
				}
				const handlePageComplete = (completedPage: number) => {
					if (completedPage !== page) return
					dv.removeEventListener("pageComplete", handlePageComplete)
					if (isPrimaryPane) instance.UI.setZoomLevel("100%")
					else dv.zoomTo(1)
				}
				dv.addEventListener("pageComplete", handlePageComplete)
			}
			dv.setCurrentPage(page, true)
		},
		[getDocViewer, instanceRef, isPrimaryPane, isSplitActive],
	)

	const handlePageNameNavigate = useCallback(
		(name: string): boolean => {
			const trimmed = name.trim()
			if (trimmed === "") return false
			const lower = trimmed.toLowerCase()
			const match = relatedObjectsRef.current?.find((p) => p.page_name != null && p.page_name.toLowerCase() === lower)
			if (!match) return false
			handlePageChange(match.page_number + 1)
			return true
		},
		[handlePageChange, relatedObjectsRef],
	)

	// Render the page-nav bar into its shadow-DOM overlay.
	useEffect(() => {
		const entry = pageNavEntryRef.current
		if (!entry) return

		entry.container.style.opacity = isPageNavVisible ? "1" : "0"
		entry.container.style.pointerEvents = isPageNavVisible ? "auto" : "none"

		const shadowRoot = getShadowRoot()
		if (!shadowRoot) return

		if (!isEnabled || !isDocumentLoaded || totalPages === 0) {
			safeRender(pageNavEntryRef, null)
			return
		}

		safeRender(
			pageNavEntryRef,
			<StyleProvider container={shadowRoot}>
				<PageNavigationBar
					currentPage={currentPage}
					totalPages={totalPages}
					pageName={currentPageName}
					onPageChange={handlePageChange}
					onPageNameNavigate={handlePageNameNavigate}
				/>
			</StyleProvider>,
		)
	}, [
		pageNavEntryRef,
		getShadowRoot,
		isEnabled,
		isDocumentLoaded,
		isPageNavVisible,
		currentPage,
		totalPages,
		currentPageName,
		handlePageChange,
		handlePageNameNavigate,
	])

	// Reset the bar when the modal closes — the viewer instance is reused across quotations, so
	// page counts / timers must not leak into the next open.
	useEffect(() => {
		if (!isModalOpen && instanceRef.current) {
			setCurrentPage(1)
			setTotalPages(0)
			resetPageNavTimers()
		}
	}, [isModalOpen, instanceRef, resetPageNavTimers])

	// Unmount cleanup — drop timers + the Core listeners (the Core listeners registered above are
	// removed by the orchestrator's UI.dispose(); the overlay root is disposed there too).
	useEffect(() => {
		return () => {
			resetPageNavTimers()
			detachListenersRef.current?.()
			detachListenersRef.current = null
		}
	}, [resetPageNavTimers])

	// currentPage/totalPages stay internal (only the render effect reads them); the orchestrator
	// needs isPageNavVisible (drives overlay reposition) and registerPageNavListeners (called in init).
	return { isPageNavVisible, registerPageNavListeners }
}
